// Application Deploy/Build API
// Extracted from applicationApi.ts — deploy and build-related API calls.

import {
  ApplicationEvents,
  ApplicationEndpoints,
} from "@generated/application";
import { apiBaseUrls } from "src/platform/constants/shared-constants";
import { EventBus } from "src/platform/events/EventBus";
import API from "src/platform/http/shared-http";
import debugLogger from "src/platform/utils/debugLogger";

import type { ApplicationEventPayload } from "../types/application.types";

export async function deployApplication(
  eventBus: EventBus,
  alias: string,
  environment?: string,
  images?: Record<string, string>,
): Promise<{ id: string; status: string }> {
  try {
    debugLogger.info("application-api", `Deploying application: ${alias}`);

    const api = new API(
      apiBaseUrls.BACKEND,
      ApplicationEndpoints.DEPLOY_APPLICATION.replace(
        ":id",
        encodeURIComponent(alias),
      ),
    );
    const body: Record<string, unknown> = {
      environment: environment || "production",
    };
    if (images && Object.keys(images).length > 0) {
      body.images = images;
    }
    const response = await api.put<{ id: string; status: string }>(body);

    eventBus.emitGlobal(ApplicationEvents.APPLICATION_DEPLOYED, {
      applicationAlias: alias,
    } as ApplicationEventPayload);

    debugLogger.info("application-api", `Application deployed: ${alias}`);
    return response;
  } catch (error) {
    const errorMessage = `Failed to deploy application: ${alias}`;
    debugLogger.error("application-api", errorMessage, error);

    eventBus.emitGlobal(ApplicationEvents.APPLICATION_ERROR, {
      error: errorMessage,
    } as ApplicationEventPayload);

    throw error;
  }
}

export async function deployOnly(
  eventBus: EventBus,
  alias: string,
  images?: Record<string, string>,
): Promise<{
  jobId: string;
  status: string;
  systemAlias: string;
  images?: Record<string, string>;
}> {
  try {
    debugLogger.info(
      "application-api",
      `Deploy-only for application: ${alias}`,
    );

    const api = new API(
      apiBaseUrls.BACKEND,
      ApplicationEndpoints.DEPLOY_ONLY.replace(
        ":id",
        encodeURIComponent(alias),
      ),
    );
    const body: Record<string, unknown> = {};
    if (images && Object.keys(images).length > 0) {
      body.images = images;
    }
    const response = await api.put<{
      jobId: string;
      status: string;
      systemAlias: string;
      images?: Record<string, string>;
    }>(body);

    eventBus.emitGlobal(ApplicationEvents.APPLICATION_DEPLOYED, {
      applicationAlias: alias,
    } as ApplicationEventPayload);

    debugLogger.info("application-api", `Deploy-only queued: ${alias}`);
    return response;
  } catch (error) {
    const errorMessage = `Failed to deploy-only application: ${alias}`;
    debugLogger.error("application-api", errorMessage, error);

    eventBus.emitGlobal(ApplicationEvents.APPLICATION_ERROR, {
      error: errorMessage,
    } as ApplicationEventPayload);

    throw error;
  }
}

export async function buildApplication(
  eventBus: EventBus,
  alias: string,
): Promise<{
  jobId: string;
  status: string;
  systemAlias: string;
  sha: string;
}> {
  try {
    debugLogger.info("application-api", `Building application: ${alias}`);

    const api = new API(
      apiBaseUrls.BACKEND,
      ApplicationEndpoints.BUILD_APPLICATION.replace(
        ":id",
        encodeURIComponent(alias),
      ),
    );
    const response = await api.put<{
      jobId: string;
      status: string;
      systemAlias: string;
      sha: string;
    }>({});

    eventBus.emitGlobal(ApplicationEvents.APPLICATION_DEPLOYED, {
      applicationAlias: alias,
    } as ApplicationEventPayload);

    debugLogger.info("application-api", `Build queued: ${alias}`);
    return response;
  } catch (error) {
    const errorMessage = `Failed to build application: ${alias}`;
    debugLogger.error("application-api", errorMessage, error);

    eventBus.emitGlobal(ApplicationEvents.APPLICATION_ERROR, {
      error: errorMessage,
    } as ApplicationEventPayload);

    throw error;
  }
}

export async function buildAndDeploy(
  eventBus: EventBus,
  alias: string,
): Promise<{
  jobId: string;
  status: string;
  systemAlias: string;
  sha: string;
}> {
  try {
    debugLogger.info("application-api", `Build & deploy application: ${alias}`);

    const api = new API(
      apiBaseUrls.BACKEND,
      ApplicationEndpoints.BUILD_AND_DEPLOY.replace(
        ":id",
        encodeURIComponent(alias),
      ),
    );
    const response = await api.put<{
      jobId: string;
      status: string;
      systemAlias: string;
      sha: string;
    }>({});

    eventBus.emitGlobal(ApplicationEvents.APPLICATION_DEPLOYED, {
      applicationAlias: alias,
    } as ApplicationEventPayload);

    debugLogger.info("application-api", `Build & deploy queued: ${alias}`);
    return response;
  } catch (error) {
    const errorMessage = `Failed to build & deploy application: ${alias}`;
    debugLogger.error("application-api", errorMessage, error);

    eventBus.emitGlobal(ApplicationEvents.APPLICATION_ERROR, {
      error: errorMessage,
    } as ApplicationEventPayload);

    throw error;
  }
}

export async function retryBuild(
  eventBus: EventBus,
  alias: string,
): Promise<{
  jobId: string;
  status: string;
  systemAlias: string;
  sha: string;
}> {
  try {
    debugLogger.info("application-api", `Retrying build for: ${alias}`);

    const api = new API(
      apiBaseUrls.BACKEND,
      ApplicationEndpoints.RETRY_BUILD_APPLICATION.replace(
        ":id",
        encodeURIComponent(alias),
      ),
    );
    const response = await api.put<{
      jobId: string;
      status: string;
      systemAlias: string;
      sha: string;
    }>({});

    eventBus.emitGlobal(ApplicationEvents.APPLICATION_DEPLOYED, {
      applicationAlias: alias,
    } as ApplicationEventPayload);

    debugLogger.info("application-api", `Build retry queued: ${alias}`);
    return response;
  } catch (error) {
    const errorMessage = `Failed to retry build: ${alias}`;
    debugLogger.error("application-api", errorMessage, error);

    eventBus.emitGlobal(ApplicationEvents.APPLICATION_ERROR, {
      error: errorMessage,
    } as ApplicationEventPayload);

    throw error;
  }
}

export async function forceFailBuild(
  eventBus: EventBus,
  alias: string,
): Promise<{ success: boolean }> {
  try {
    debugLogger.info("application-api", `Force-failing build for: ${alias}`);

    const api = new API(
      apiBaseUrls.BACKEND,
      ApplicationEndpoints.FORCE_FAIL_BUILD.replace(
        ":id",
        encodeURIComponent(alias),
      ),
    );
    const response = await api.put<{ success: boolean }>({});

    debugLogger.info("application-api", `Build force-failed: ${alias}`);
    return response;
  } catch (error) {
    const errorMessage = `Failed to force-fail build: ${alias}`;
    debugLogger.error("application-api", errorMessage, error);

    eventBus.emitGlobal(ApplicationEvents.APPLICATION_ERROR, {
      error: errorMessage,
    } as ApplicationEventPayload);

    throw error;
  }
}
